import { commands } from '@uiw/react-md-editor';

/*
 * Custom MDEditor toolbar commands for media upload.
 *
 * The upload flow itself (hidden file inputs + handleMediaUpload with blob
 * URLs and pendingMediaRef) stays in the page components — these commands
 * only trigger the corresponding hidden <input type="file"> by id, exactly
 * like the old external label buttons did.
 */
function mediaCommand(name, label, faIcon, inputId) {
  return {
    name,
    keyCommand: name,
    buttonProps: { 'aria-label': label, title: label },
    icon: <i className={`fa-solid ${faIcon}`} aria-hidden="true" />,
    execute: () => {
      document.getElementById(inputId)?.click();
    },
  };
}

const faIcon = (name) => <i className={`fa-solid ${name}`} aria-hidden="true" />;

/** A built-in command with the icon the visual editor uses for the same thing. */
const withIcon = (command, name) => ({ ...command, icon: faIcon(name) });

/*
 * Full toolbar, in the visual editor's order and with its icons
 * (components/VisualEditor.js): text marks, headings, lists, code and
 * blocks, then our three upload commands instead of the built-in template
 * "image" command (it only inserts an empty ![](url) skeleton). The
 * built-in "comment" and "help" (a link to an outside page) are left out.
 *
 * inputIds: { image, video, audio } - ids of the hidden file inputs.
 */
export function buildEditorCommands(inputIds) {
  return [
    withIcon(commands.bold, 'fa-bold'),
    withIcon(commands.italic, 'fa-italic'),
    withIcon(commands.strikethrough, 'fa-strikethrough'),
    withIcon(commands.link, 'fa-link'),
    commands.divider,
    commands.group([commands.title2, commands.title3, commands.title4], {
      name: 'title',
      groupName: 'title',
      buttonProps: { 'aria-label': 'Überschrift', title: 'Überschrift' },
      icon: faIcon('fa-heading'),
    }),
    commands.divider,
    withIcon(commands.unorderedListCommand, 'fa-list-ul'),
    withIcon(commands.orderedListCommand, 'fa-list-ol'),
    withIcon(commands.checkedListCommand, 'fa-list-check'),
    withIcon(commands.quote, 'fa-quote-right'),
    commands.divider,
    withIcon(commands.code, 'fa-terminal'),
    withIcon(commands.codeBlock, 'fa-file-code'),
    withIcon(commands.hr, 'fa-minus'),
    withIcon(commands.table, 'fa-table'),
    commands.divider,
    mediaCommand('upload-image', 'Bild einfügen', 'fa-image', inputIds.image),
    mediaCommand('upload-video', 'Video einfügen', 'fa-video', inputIds.video),
    mediaCommand('upload-audio', 'Audio einfügen', 'fa-volume-high', inputIds.audio),
    // "Neu importieren" (components/ReimportDialog.js): only where the page
    // passes the id of the hidden button that opens the dialog.
    ...(inputIds.reimport
      ? [
        commands.divider,
        mediaCommand('reimport', 'Neu importieren (PDF oder Kurs)', 'fa-file-import', inputIds.reimport),
      ]
      : []),
  ];
}

/** The right end of the toolbar: source only, side by side, preview only, full screen (their own icons). */
export const EDITOR_EXTRA_COMMANDS = [
  commands.codeEdit,
  commands.codeLive,
  commands.codePreview,
  commands.divider,
  commands.fullscreen,
];
